import React, { useState } from 'react';
import { Check, ChevronRight, Star, Layout, PenTool, Settings, ArrowRight } from 'lucide-react';
import img1 from "../../assets/kitchen.jpg";
import img2 from "../../assets/kitchen1.jpg";
import img3 from "../../assets/kitchen2.jpg";
import img4 from "../../assets/kitchen3.jpg";
import img5 from "../../assets/kitchen5.jpg";
import img6 from "../../assets/kitchen4.jpg";
import img7 from "../../assets/kitchen6.jpg";
import img8 from "../../assets/kitchen7.jpg";
import img9 from "../../assets/kitchen8.jpg";
import img10 from "../../assets/kitchen9.jpg";
import img11 from "../../assets/kitchen10.jpg";
import img12 from "../../assets/kitchen11.jpg";
import img13 from "../../assets/kitchen12.jpg";
import img14 from "../../assets/kitchen3.jpg";
// Data for different Kitchen Styles (Real Images)
const kitchenDesigns = {
  modern: [
    { 
      id: 1, 
      img: img1, 
      title: 'Midnight Luxe', 
      desc: 'Matte Black • Handleless' 
    },
    { 
      id: 2, 
      img: img2, 
      title: 'Scandi Pure', 
      desc: 'White & Oak • Minimalist' 
    },
    { 
      id: 3, 
      img: img3, 
      title: 'Urban Grey', 
      desc: 'High Gloss • Acrylic' 
    }
  ],
  classic: [
    { 
      id: 1, 
      img: img4, 
      title: 'Royal Heritage', 
      desc: 'Teak Wood • Grooved Shutters' 
    },
    { 
      id: 2, 
      img: img5, 
      title: 'Vintage White', 
      desc: 'Glass Cabinets • Brass Handles' 
    },
    { 
      id: 3, 
      img: img6, 
      title: 'Country Charm', 
      desc: 'Cream Finish • Stone Counter' 
    }
  ],
  island: [
    { 
      id: 1, 
      img: img7, 
      title: 'The Social Hub', 
      desc: 'Marble Island • Breakfast Counter' 
    },
    { 
      id: 2, 
      img: img8, 
      title: 'Open Concept', 
      desc: 'Integrated Sink • Bar Seating' 
    },
    { 
      id: 3, 
      img: img9, 
      title: 'Chef’s Paradise', 
      desc: 'Central Hob • Butcher Block' 
    }
  ]
};

const Kitchen = () => {
  const [activeTab, setActiveTab] = useState('modern');

  // Helper to get current images based on tab
  const currentImages = kitchenDesigns[activeTab as keyof typeof kitchenDesigns];

  return (
    <div className="font-sans bg-white  text-neutral-900">
      
      {/* 1. HERO SECTION */}
      
        <div 
        className="relative h-[60vh] w-full bg-cover bg-center flex items-center justify-center  text-center bg-neutral-900"
        style={{ backgroundImage: `url(${img14})` }}
      >
        
        <div className="relative z-10 text-center px-6 animate-fadeIn mt-10">
          <span className="text-amber-400 tracking-[0.3em] text-xs font-bold uppercase mb-4 block">
            Culinary Masterpieces
          </span>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            The Modern Kitchen
          </h1>
          <p className="text-gray-200 text-lg max-w-2xl mx-auto  leading-relaxed">
            Designed for the chef in you. Engineered for a lifetime.
          </p>
        </div>
      </div>

      {/* 2. LAYOUT SELECTOR */}
      <div className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-2">Know Your Space</h4>
          <h2 className="text-3xl md:text-4xl font-serif text-neutral-900">Popular Kitchen Layouts</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { title: "L-Shaped", desc: "Best for corners.", img:  img10},
            { title: "U-Shaped", desc: "Max storage.", img: img11},
            { title: "Straight", desc: "For studios.", img: img12},
            { title: "Island", desc: "Social cooking.", img: img13 },
          ].map((layout, idx) => (
            <div key={idx} className="group cursor-pointer">
              <div className="overflow-hidden rounded-sm mb-4 relative h-64">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-all z-10"></div>
                <img 
                  src={layout.img} 
                  alt={layout.title} 
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" 
                />
                <div className="absolute bottom-4 left-4 text-white z-20">
                  <h3 className="text-xl font-serif font-bold">{layout.title}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. PREMIUM FINISHES */}
      <div className="bg-neutral-50 py-24 px-6 border-y border-gray-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-16">
          <div className="w-full md:w-1/2">
             <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-3">Material Palette</h4>
             <h2 className="text-4xl font-serif text-neutral-900 mb-6">Finishes that Define Luxury</h2>
             <p className="text-gray-600 leading-relaxed mb-8">
               Your kitchen's personality comes from its finish. Whether you prefer the mirror-like reflection of Acrylic or the understated elegance of Matte, we use only premium, scratch-resistant materials.
             </p>
             <div className="space-y-6">
               {[
                 { name: "High Gloss Acrylic", desc: "Mirror finish, easy to clean, makes space look bigger." },
                 { name: "Soft Touch PU", desc: "Velvety matte feel, highly durable, premium look." },
                 { name: "Veneer Wood", desc: "Natural wood texture for a warm, classic appeal." }
               ].map((item, idx) => (
                 <div key={idx} className="flex gap-4">
                   <div className="w-12 h-12 bg-white border border-gray-200 flex items-center justify-center shrink-0 rounded-full shadow-sm text-amber-600">
                     <Check size={20} />
                   </div>
                   <div>
                     <h4 className="font-bold text-neutral-900">{item.name}</h4>
                     <p className="text-sm text-gray-500">{item.desc}</p>
                   </div>
                 </div>
               ))}
             </div>
          </div>
          <div className="w-full md:w-1/2 grid grid-cols-2 gap-4">
             <img src="https://www.oppoliahome.com/wp-content/uploads/2022/12/dark-grey-cabinets-with-black-worktop-obk22-m03-1.jpg" className="rounded-lg shadow-lg translate-y-8 h-80 object-cover" alt="Finish 1"/>
             <img src="https://cloudfrontgharpediabucket.gharpedia.com/uploads/2023/07/Luxury-Kitchen-Designs-01-0502010063.jpg" className="rounded-lg shadow-lg h-80 object-cover" alt="Finish 2"/>
          </div>
        </div>
      </div>

      {/* 4. CURATED GALLERY (UPDATED DESIGN) */}
      <div className="py-24 px-6 max-w-7xl mx-auto">
        
        {/* Header & Tabs */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-12">
          <div>
            <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-2">Inspiration</h4>
            <h2 className="text-3xl md:text-4xl font-serif text-neutral-900">Curated Collections</h2>
          </div>
          
          <div className="flex gap-8 mt-6 md:mt-0 border-b border-gray-200 pb-1">
             {Object.keys(kitchenDesigns).map((tab) => (
               <button 
                 key={tab}
                 onClick={() => setActiveTab(tab)}
                 className={`text-sm uppercase tracking-widest px-2 py-2 transition-all relative ${
                   activeTab === tab ? 'text-amber-600 font-bold' : 'text-gray-400 hover:text-black'
                 }`}
               >
                 {tab}
                 {/* Active Underline Animation */}
                 {activeTab === tab && (
                   <span className="absolute bottom-[-5px] left-0 w-full h-[2px] bg-amber-600"></span>
                 )}
               </button>
             ))}
          </div>
        </div>

        {/* Dynamic Image Collage Layout */}
        <div className="flex flex-col md:flex-row gap-6 h-auto md:h-[550px]">
          
          {/* Large Image (Left Side) - Takes 65% Width */}
          <div className="w-full md:w-[65%] h-[400px] md:h-full group relative overflow-hidden rounded-sm cursor-pointer">
            <img 
              src={currentImages[0].img} 
              alt={currentImages[0].title}
              className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90"></div>
            <div className="absolute bottom-8 left-8 text-white animate-fadeIn">
              <span className="text-amber-400 text-xs uppercase tracking-widest mb-2 block">{currentImages[0].desc}</span>
              <h3 className="text-4xl font-serif">{currentImages[0].title}</h3>
              <button className="mt-4 flex items-center gap-2 text-sm uppercase tracking-widest hover:text-amber-400 transition-colors">
                View Project <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* Medium Images (Right Side) - Takes 35% Width & Stacks Vertically */}
          <div className="w-full md:w-[35%] flex flex-col gap-6 h-full">
            
            {/* Top Small Image */}
            <div className="h-1/2 group relative overflow-hidden rounded-sm cursor-pointer">
              <img 
                src={currentImages[1].img} 
                alt={currentImages[1].title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all"></div>
              <div className="absolute bottom-6 left-6 text-white">
                 <h4 className="text-xl font-serif">{currentImages[1].title}</h4>
                 <p className="text-xs text-gray-300 uppercase tracking-widest">{currentImages[1].desc}</p>
              </div>
            </div>

            {/* Bottom Small Image */}
            <div className="h-1/2 group relative overflow-hidden rounded-sm cursor-pointer">
              <img 
                src={currentImages[2].img} 
                alt={currentImages[2].title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all"></div>
              <div className="absolute bottom-6 left-6 text-white">
                 <h4 className="text-xl font-serif">{currentImages[2].title}</h4>
                 <p className="text-xs text-gray-300 uppercase tracking-widest">{currentImages[2].desc}</p>
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* 5. CTA SECTION */}
      <div className="bg-neutral-900 text-white py-20 text-center">
         <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-serif mb-6">Ready to Cook in Style?</h2>
            <p className="text-gray-400 mb-8">Get a free consultation and 3D design for your kitchen today.</p>
            <button className="bg-amber-600 text-white px-10 py-4 text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-neutral-900 transition-all shadow-xl">
               Book Free Design Visit
            </button>
         </div>
      </div>
        
    </div>
  );
};

export default Kitchen;